import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MarketingWebsiteSeoService } from '@core/seo/marketing-website-seo.service';
import { PUBLIC_BRAND_LOGO_SRC, PUBLIC_BRAND_WORDMARK } from '@core/branding/public-brand';
import { buildWebsiteMarketingSeoConfig } from '../../../utils/website-marketing-seo.utils';
import {
  canonicalizeWebsitePlanCode,
  patchWebsiteClaim,
  readWebsiteClaim,
  type WebsitePlanCode,
} from '../../../utils/website-claim.storage';
import { WebsiteMarketingPathService } from '../../../services/website-marketing-path.service';
import { isUsableWebsiteSlug } from '../../../utils/website-slug.utils';
import { WebsitePublishedPlansService } from '../../../services/website-published-plans.service';
import { WebsitePublishedPlan } from '../../../models/website-published-plan.model';
import {
  fallbackWebsitePublishedPlans,
  formatWebsitePlanPrice,
  websitePaidTrialDays,
  websitePlanFeatureKeys,
} from '../../../utils/website-published-plan.utils';
import { websiteRegisterHref } from '../../../utils/website-auth-urls';

@Component({
  selector: 'app-website-plan-select-page',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  templateUrl: './website-plan-select-page.component.html',
  styleUrls: ['./website-plan-select-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WebsitePlanSelectPageComponent implements OnInit {
  private readonly marketingSeo = inject(MarketingWebsiteSeoService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly publishedPlans = inject(WebsitePublishedPlansService);
  readonly mktPath = inject(WebsiteMarketingPathService);

  readonly brandLogoSrc = PUBLIC_BRAND_LOGO_SRC;
  readonly brandWordmark = PUBLIC_BRAND_WORDMARK;
  readonly publicHost = 'milinco.app';

  readonly selected = signal<WebsitePlanCode | null>(null);
  readonly claimSlug = signal('');
  readonly claimDisplayName = signal('');
  readonly plans = signal<WebsitePublishedPlan[]>(fallbackWebsitePublishedPlans());
  readonly paidTrialDays = computed(() => websitePaidTrialDays(this.plans()));
  readonly selectedPlan = computed(() => {
    const code = this.selected();
    return this.plans().find((plan) => plan.code === code) ?? null;
  });
  readonly selectedPlanLabel = computed(() => {
    const plan = this.selectedPlan();
    if (!plan) {
      return this.translate.instant('website.planSelect.selectedEmpty');
    }
    return plan.marketingName;
  });
  readonly ctaLabel = computed(() => {
    const plan = this.selectedPlan();
    if (plan?.salesLed) {
      return this.translate.instant('website.plans.enterpriseCta');
    }
    return this.translate.instant('website.planSelect.cta');
  });

  private claimName = '';

  ngOnInit(): void {
    this.applySeo();
    this.destroyRef.onDestroy(() => this.marketingSeo.clear());
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.applySeo());
    this.publishedPlans
      .list()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((rows) => {
        if (rows.length) {
          this.plans.set(rows);
        }
      });

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const claim = readWebsiteClaim();
    if (!claim?.name?.trim() || !isUsableWebsiteSlug(claim.slug)) {
      void this.router.navigate(this.mktPath.home());
      return;
    }

    this.claimName = claim.name.trim();
    this.claimDisplayName.set(this.claimName);
    this.claimSlug.set(claim.slug);
    // Default self-serve sin claim = Free (Link).
    this.selected.set(claim.planCode === '' ? 'link' : claim.planCode);
  }

  planPrice(plan: WebsitePublishedPlan): string {
    return formatWebsitePlanPrice(
      plan.priceMonthlyCents,
      plan.currency,
      this.translate.instant('website.plans.link.price')
    );
  }

  planFeatureKeys(code: string): string[] {
    return websitePlanFeatureKeys(code);
  }

  selectPlan(code: string): void {
    const canonical = canonicalizeWebsitePlanCode(code);
    if (canonical) {
      this.selected.set(canonical);
    }
  }

  continue(): void {
    const plan = this.selectedPlan();
    const slug = this.claimSlug();
    if (!plan || !this.claimName || !slug) {
      return;
    }
    if (plan.salesLed) {
      void this.router.navigate(this.mktPath.home(), { fragment: 'contacto' });
      return;
    }
    const planCode = canonicalizeWebsitePlanCode(plan.code);
    if (!planCode) {
      return;
    }
    const claim = patchWebsiteClaim({ planCode });
    globalThis.location.assign(websiteRegisterHref(claim));
  }

  private applySeo(): void {
    this.marketingSeo.apply(
      buildWebsiteMarketingSeoConfig(
        'plan',
        {
          docTitle: this.translate.instant('website.planSelect.docTitle'),
          metaDescription: this.translate.instant('website.planSelect.metaDescription'),
          heroTitle: this.translate.instant('website.planSelect.docTitle'),
          heroSub: this.translate.instant('website.planSelect.metaDescription'),
        },
        typeof window !== 'undefined' ? window.location.origin : undefined
      )
    );
  }
}
